import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/utils";
import { saveAuthSession } from "@/lib/auth-session";
import { isPlatformSession } from "@/lib/platform-api";
import AuthPageShell from "@/components/AuthPageShell";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import { verifyIdentity } from "@/lib/social-auth";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = searchParams.get("return") || "";
  const sessionReason = searchParams.get("session") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [teamEmails, setTeamEmails] = useState([]);
  const [ssoStatus, setSsoStatus] = useState("not_configured");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sessionReason === "expired") {
      toast.message("Session expired", {
        description: "Sign in again to continue where you left off.",
      });
    }
  }, [sessionReason]);

  useEffect(() => {
    const hashParams = new URLSearchParams(
      (typeof window !== "undefined" && window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : "") || "",
    );
    const ssoToken = hashParams.get("sso_token") || searchParams.get("sso_token");
    if (!ssoToken) return;
    saveAuthSession({ token: ssoToken, email: "sso@workspace" });
    if (typeof window !== "undefined" && window.location.hash.includes("sso_token")) {
      window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
    }
    toast.success("Signed in with SSO");
    const dest = returnPath && returnPath.startsWith("/") ? returnPath : isPlatformSession() ? "/platform" : "/admin";
    navigate(dest, { replace: true });
  }, [searchParams, navigate, returnPath]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bootstrapRes, brandingRes] = await Promise.all([
          fetch(`${API_BASE}/api/bootstrap-data`),
          fetch(`${API_BASE}/api/public/workspace-branding?host=${encodeURIComponent(window.location.hostname || "")}`),
        ]);
        if (bootstrapRes.ok) {
          const payload = await bootstrapRes.json();
          const rows = Array.isArray(payload?.data?.team) ? payload.data.team : [];
          if (!cancelled) setTeamEmails(rows.map((r) => String(r?.email || "").toLowerCase()).filter(Boolean));
        }
        if (brandingRes.ok) {
          const payload = await brandingRes.json();
          const status = String(payload?.ssoStatus || "").trim().toLowerCase();
          if (!cancelled) {
            if (status) setSsoStatus(status);
            else setSsoStatus("not_configured");
          }
        }
      } catch {
        // leave defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const cleanedEmail = String(email || "").trim().toLowerCase();
    if (!cleanedEmail || !password) {
      toast.error("Missing credentials", { description: "Enter both email and password." });
      return;
    }
    try {
      setBusy(true);
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: cleanedEmail, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.detail || `Sign in failed (${res.status})`);
      }
      if (!body?.token) {
        throw new Error("Login succeeded but no session token was returned. Try again or contact support.");
      }
      if (teamEmails.length && !teamEmails.includes(cleanedEmail)) {
        toast.message("External operator", {
          description: "Email is not in bootstrap team list; access granted via credentials.",
        });
      }
      saveAuthSession({
        email: body.email || cleanedEmail,
        token: body.token,
        via: "password",
        role: body.role || "admin",
        tenant: body.tenant,
      });
      const role = body.role || "admin";
      const defaultDest = role === "platform_admin" ? "/platform" : "/admin";
      const dest =
        returnPath && returnPath.startsWith("/") && !returnPath.startsWith("/login") ? returnPath : defaultDest;
      toast.success(`Welcome back, ${body.email || cleanedEmail}`, {
        description: role === "platform_admin" ? "Platform console" : "Loading workspace",
      });
      setTimeout(() => navigate(dest), 200);
    } catch (err) {
      toast.error("Sign in failed", { description: err?.message || "Invalid email or password." });
    } finally {
      setBusy(false);
    }
  };

  const finishSession = (body, via) => {
    saveAuthSession({
      email: body.email,
      token: body.token,
      via,
      role: body.role || "admin",
      tenant: body.tenant,
    });
    const role = body.role || "admin";
    const defaultDest = role === "platform_admin" ? "/platform" : "/admin";
    const dest =
      returnPath && returnPath.startsWith("/") && !returnPath.startsWith("/login") ? returnPath : defaultDest;
    toast.success(`Welcome back, ${body.email}`, {
      description: role === "platform_admin" ? "Platform console" : "Loading workspace",
    });
    setTimeout(() => navigate(dest), 200);
  };

  const handleSocialToken = async (provider, idToken) => {
    try {
      setBusy(true);
      const body = await verifyIdentity({ provider, idToken, intent: "login" });
      finishSession(body, provider);
    } catch (err) {
      if (err.code === "password_or_link_required") {
        toast.error("Link this provider in Settings", {
          description: "Sign in with your password, then connect Google or Apple under Settings → Security.",
        });
        if (err.email) setEmail(err.email);
        return;
      }
      if (err.code === "no_account") {
        toast.message("No account yet", { description: "Start a trial or ask your admin for an invite." });
        return;
      }
      toast.error("Sign in failed", { description: err?.message || "Could not verify provider." });
    } finally {
      setBusy(false);
    }
  };

  const continueWithSso = () => {
    if (ssoStatus !== "active") {
      toast.error("SSO not active", {
        description: "Set OIDC issuer and SSO status to Active under Settings → Security, or use password login.",
      });
      return;
    }
    window.location.href = `${API_BASE}/api/auth/sso/start`;
  };

  return (
    <AuthPageShell>
      <form onSubmit={submit} data-testid="login-form" className="auth-login-form">
        <h1 className="text-[2rem] sm:text-[2.25rem] font-semibold tracking-tight text-[#0a0a0a] leading-tight">
          Welcome Back!
        </h1>
        <p className="mt-2 text-[15px] text-[#6b7280]">Enter your details below.</p>

        {sessionReason === "expired" ? (
          <div
            className="mt-6 auth-shell-banner p-4 border-l-2 border-l-[#111] text-sm text-[var(--text-secondary)]"
            role="status"
          >
            Your session expired. Sign in again to return to the admin console.
          </div>
        ) : null}

        <div className="mt-10 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#111827] mb-1">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-login-input"
              placeholder="name@example.com"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#111827] mb-1">Password</label>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-login-input pr-9"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="absolute right-0 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#111827] p-1"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm pt-1">
            <label className="flex items-center gap-2 text-[#4b5563] cursor-pointer select-none">
              <input type="checkbox" className="auth-login-check" defaultChecked />
              Remember me
            </label>
            <Link to="/forgot-password" className="text-[#111827] font-medium hover:underline">
              Forgot password?
            </Link>
          </div>

          <button type="submit" className="auth-login-primary w-full mt-1" data-testid="login-submit" disabled={busy}>
            {busy ? "Signing in..." : "Log in"}
          </button>

          <SocialAuthButtons
            intent="login"
            disabled={busy}
            onProviderToken={handleSocialToken}
            className="auth-login-social"
            showDivider={false}
          />

          {ssoStatus !== "not_configured" ? (
            <button
              type="button"
              className="auth-login-secondary w-full"
              data-testid="login-sso"
              onClick={continueWithSso}
              disabled={busy || ssoStatus !== "active"}
              title={ssoStatus !== "active" ? "Configure SSO under Settings → Security" : "Start OIDC"}
            >
              Continue with SSO
              {ssoStatus !== "active" ? (
                <span className="ml-2 text-[10px] font-mono text-[#9ca3af]">({ssoStatus})</span>
              ) : null}
            </button>
          ) : null}
        </div>

        <p className="mt-8 text-center text-sm text-[#6b7280]">
          Don&apos;t have an account?{" "}
          <Link to="/signup" className="text-[#111827] font-semibold hover:underline">
            Sign Up
          </Link>
        </p>
      </form>
    </AuthPageShell>
  );
}
